import { useState, useEffect, useCallback, useRef } from 'react';
import type { Event, Room, Timeslot, Template, Proposal, Session, Vote, ScheduleItem, PublishedVersion } from '../types';
import * as api from '../lib/api';
import { db, getAccessCode, setAccessCode, getOrganizerToken, setOrganizerToken, getOrCreateDeviceId, getNickname } from '../lib/db';

interface UseEventResult {
  event: Event | null;
  rooms: Room[];
  timeslots: Timeslot[];
  template: Template | null;
  proposals: Proposal[];
  sessions: Session[];
  scheduleItems: ScheduleItem[];
  myVotes: Vote[];
  publishedVersion: PublishedVersion | null;
  isLoading: boolean;
  error: string | null;
  isOrganizer: boolean;
  hasAccessCode: boolean;
  /** Current device ID (for ownership checks). */
  deviceId: string | null;
  /** Current user nickname. */
  nickname: string | null;
  refresh: () => Promise<void>;
  loginOrganizer: (password: string) => Promise<boolean>;
  verifyAccess: (code: string) => Promise<boolean>;
}

/**
 * Hook to load and manage event data
 */
export function useEvent(eventId: string | null): UseEventResult {
  const [event, setEvent] = useState<Event | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [template, setTemplate] = useState<Template | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [myVotes, setMyVotes] = useState<Vote[]>([]);
  const [publishedVersion, setPublishedVersion] = useState<PublishedVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [hasAccessCode, setHasAccessCode] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [nickname, setNicknameState] = useState<string | null>(null);

  // Track whether we've completed an initial load to avoid flickering on refresh
  const hasLoadedOnce = useRef(false);

  // Load device ID and nickname once
  useEffect(() => {
    getOrCreateDeviceId().then(setDeviceId);
    getNickname().then(setNicknameState);
  }, []);

  const loadData = useCallback(async () => {
    if (!eventId) {
      setIsLoading(false);
      return;
    }

    // Only show loading spinner on first load, not on background refreshes
    if (!hasLoadedOnce.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Check auth state
      const [accessCode, organizerToken] = await Promise.all([
        getAccessCode(eventId),
        getOrganizerToken(eventId),
      ]);
      setHasAccessCode(!!accessCode);
      setIsOrganizer(!!organizerToken);

      // Load all data in parallel
      const [
        eventData,
        roomsData,
        timeslotsData,
        templateData,
        proposalsData,
        sessionsData,
        scheduleData,
        votesData,
        publishedData,
      ] = await Promise.all([
        api.fetchEvent(eventId),
        api.fetchRooms(eventId),
        api.fetchTimeslots(eventId),
        api.fetchTemplate(eventId),
        api.fetchProposals(eventId),
        api.fetchSessions(eventId),
        api.fetchSchedule(eventId),
        api.fetchMyVotes(eventId),
        api.fetchPublishedVersion(eventId),
      ]);

      setEvent(eventData);
      setRooms(roomsData);
      setTimeslots([...timeslotsData].sort((a, b) => (a.indexInDay ?? 0) - (b.indexInDay ?? 0)));
      setTemplate(templateData);
      setProposals(proposalsData);
      setSessions(sessionsData);
      setScheduleItems(scheduleData);
      setMyVotes(votesData);
      setPublishedVersion(publishedData);

      hasLoadedOnce.current = true;

      // Cache to IndexedDB
      await Promise.all([
        db.events.put(eventData),
        db.rooms.bulkPut(roomsData.map(r => ({ ...r, eventId }))),
        db.timeslots.bulkPut(timeslotsData.map(t => ({ ...t, eventId }))),
        db.proposals.bulkPut(proposalsData.map(p => ({ ...p, eventId }))),
        db.sessions.bulkPut(sessionsData.map(s => ({ ...s, eventId }))),
        db.scheduleItems.bulkPut(scheduleData.map((s: ScheduleItem) => ({ ...s, eventId }))),
        db.votes.bulkPut(votesData.map(v => ({ ...v, eventId }))),
      ]);
    } catch (err) {
      console.error('Failed to load event data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load event');

      // Try to load from IndexedDB
      try {
        const cachedEvent = await db.events.get(eventId);
        if (cachedEvent) {
          setEvent(cachedEvent);
          setRooms(await db.rooms.where('eventId').equals(eventId).toArray());
          setTimeslots(await db.timeslots.where('eventId').equals(eventId).sortBy('indexInDay'));
          setProposals(await db.proposals.where('eventId').equals(eventId).toArray());
          setSessions(await db.sessions.where('eventId').equals(eventId).toArray());
          setScheduleItems(await db.scheduleItems.where('eventId').equals(eventId).toArray());
          setMyVotes(await db.votes.where('eventId').equals(eventId).toArray());
        }
      } catch (dbErr) {
        console.error('Failed to load from cache:', dbErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lightweight event-status poll (5 s) – keeps votingOpen flag live.
  // We gate on `event` being non-null so polling only starts after initial load.
  useEffect(() => {
    if (!eventId || !event) return;

    const POLL_MS = 3000;
    const id = window.setInterval(async () => {
      try {
        const freshEvent = await api.fetchEvent(eventId);
        setEvent((prev) => {
          if (!prev) return freshEvent;
          // Only update if something changed
          if (
            prev.votingOpen !== freshEvent.votingOpen ||
            prev.name !== freshEvent.name ||
            prev.presentingProposalId !== freshEvent.presentingProposalId
          ) {
            return { ...prev, ...freshEvent };
          }
          return prev;
        });
      } catch {
        // Silently ignore polling failures
      }
    }, POLL_MS);

    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when eventId changes or event first loads
  }, [eventId, !!event]);

  const loginOrganizer = useCallback(async (password: string): Promise<boolean> => {
    if (!eventId) return false;

    try {
      const { token } = await api.loginOrganizer(eventId, password);
      await setOrganizerToken(eventId, token);
      setIsOrganizer(true);
      // Refresh nickname (it may have been set in the login modal)
      getNickname().then(setNicknameState);
      return true;
    } catch {
      return false;
    }
  }, [eventId]);

  const verifyAccess = useCallback(async (code: string): Promise<boolean> => {
    if (!eventId) return false;

    try {
      const { valid } = await api.verifyAccessCode(eventId, code);
      if (valid) {
        await setAccessCode(eventId, code);
        setHasAccessCode(true);
        // Refresh nickname (it may have been set in the access modal)
        getNickname().then(setNicknameState);
      }
      return valid;
    } catch {
      return false;
    }
  }, [eventId]);

  return {
    event,
    rooms,
    timeslots,
    template,
    proposals,
    sessions,
    scheduleItems,
    myVotes,
    publishedVersion,
    isLoading,
    error,
    isOrganizer,
    hasAccessCode,
    deviceId,
    nickname,
    refresh: loadData,
    loginOrganizer,
    verifyAccess,
  };
}
